import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import api from '../api/client';
import {
  EGE_YEAR_OPTIONS,
  FINANCE_OPTIONS,
  GOAL_OPTIONS,
  GROUP_OPTIONS,
  HOMEWORK_OPTIONS,
  SCORE_OPTIONS,
  WORKLOAD_OPTIONS,
  needsEgeDetails,
  optionLabel,
} from '../questionnaire/options';

function AnswerRow({ label, value }) {
  return (
    <div className="questionnaire-readonly-row">
      <div className="questionnaire-readonly-label">{label}</div>
      <div className="questionnaire-readonly-value">{value || '—'}</div>
    </div>
  );
}

export default function TeacherStudentQuestionnairePage() {
  const { studentId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [teacherComment, setTeacherComment] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError('');
      try {
        const res = await api.get(`/teacher/students/${studentId}/questionnaire/`);
        if (cancelled) return;
        setData(res.data);
        setTeacherComment(res.data?.teacher_comment || '');
      } catch (e) {
        if (!cancelled) {
          setError(e.response?.data?.detail || 'Не удалось загрузить анкету');
          setData(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [studentId]);

  async function saveComment(e) {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const res = await api.patch(`/teacher/students/${studentId}/questionnaire/`, {
        teacher_comment: teacherComment,
      });
      setData((prev) => ({ ...prev, ...res.data, filled: true }));
      setSuccess('Комментарий сохранён');
    } catch (err) {
      setError(err.response?.data?.detail || 'Не удалось сохранить комментарий');
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner" />
        <p>Загрузка анкеты...</p>
      </div>
    );
  }

  const student = data?.student;
  const studentName = student
    ? [student.last_name, student.first_name].filter(Boolean).join(' ') || student.username
    : '';

  return (
    <div className="questionnaire-page">
      <div className="page-header">
        <p>
          <Link to={`/teacher/students/${studentId}`} className="back-link">← К работе с учеником</Link>
        </p>
        <h1>Анкета{studentName ? `: ${studentName}` : ''}</h1>
        {student?.username && <p className="page-subtitle">@{student.username}</p>}
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {!data?.filled ? (
        <p className="text-secondary">Ученик ещё не заполнил анкету.</p>
      ) : (
        <>
          <div className="questionnaire-readonly">
            <AnswerRow
              label="1. Цель подготовки"
              value={
                data.goal === 'other' && data.goal_other
                  ? `Другое: ${data.goal_other}`
                  : optionLabel(GOAL_OPTIONS, data.goal)
              }
            />
            {needsEgeDetails(data.goal) && (
              <>
                <AnswerRow
                  label="1.2. Год сдачи ЕГЭ"
                  value={
                    data.ege_year === 'other' && data.ege_year_other
                      ? data.ege_year_other
                      : optionLabel(EGE_YEAR_OPTIONS, data.ege_year)
                  }
                />
                <AnswerRow
                  label="1.3. Целевые баллы"
                  value={optionLabel(SCORE_OPTIONS, data.target_score)}
                />
              </>
            )}
            <AnswerRow
              label="2. Отношение к ДЗ"
              value={optionLabel(HOMEWORK_OPTIONS, data.homework_attitude)}
            />
            <AnswerRow
              label="3. Загруженность"
              value={optionLabel(WORKLOAD_OPTIONS, data.workload)}
            />
            <AnswerRow
              label="4. Групповые занятия"
              value={optionLabel(GROUP_OPTIONS, data.group_attitude)}
            />
            <AnswerRow
              label="5. Финансовые возможности"
              value={optionLabel(FINANCE_OPTIONS, data.finance)}
            />
            <AnswerRow label="Комментарий ученика" value={data.comment} />
          </div>

          <form className="questionnaire-teacher-note" onSubmit={saveComment}>
            <h2>Комментарий преподавателя</h2>
            <p className="questionnaire-hint">Виден только вам, ученик его не видит.</p>
            {success && <div className="alert alert-success">{success}</div>}
            <textarea
              className="form-input"
              rows={5}
              value={teacherComment}
              onChange={(e) => setTeacherComment(e.target.value)}
            />
            <div className="form-actions">
              <button type="submit" className="btn btn-primary" disabled={saving}>
                {saving ? 'Сохранение...' : 'Сохранить комментарий'}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  );
}
